import {
  PROFILE_SKILLS as OTHER_SKILLS,
  profileSkillKeys,
  normalizeProfileSkill,
} from '../../contracts/profile-skills.generated';
import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, inject } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  IonButton,
  IonButtons,
  IonCheckbox,
  IonCol,
  IonContent,
  IonFooter,
  IonGrid,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonRadio,
  IonRadioGroup,
  IonRange,
  IonRow,
  IonTitle,
  IonToolbar,
  ModalController,
} from '@ionic/angular/standalone';

const GENDERS = ['Male', 'Female', 'Other'];
const ETHNICITIES = ['Asian', 'Black', 'Caucasian', 'Hispanic', 'Middle Eastern', 'Mixed', 'Other'];
const COMPLEXIONS = ['Fair', 'Wheatish', 'Dark', 'Other'];

type UserFilterOption = { value: string; label: string; checked: boolean };

@Component({
  selector: 'app-user-filter-modal',
  templateUrl: './user-filter-modal.component.html',
  styleUrls: ['./user-filter-modal.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    IonHeader,
    IonToolbar,
    IonTitle,
    IonButtons,
    IonButton,
    IonCheckbox,
    IonIcon,
    IonContent,
    IonItem,
    IonLabel,
    IonGrid,
    IonCol,
    IonRow,
    IonList,
    IonFooter,
    IonRadio,
    IonRadioGroup,
    IonRange,
  ],
})
export class UserFilterModalComponent implements OnInit {
  private modalCtrl: ModalController = inject(ModalController);

  @Input() filters: any;

  emailVerified: boolean | 'All' = 'All';
  subscriber: boolean | 'All' = 'All';
  verifiedAccount: boolean | 'All' = 'All';
  sortMode = 'newest';
  genderOptions: UserFilterOption[] = GENDERS.map((value) => ({ value, label: value, checked: false }));
  ethnicityOptions: UserFilterOption[] = ETHNICITIES.map((value) => ({ value, label: value, checked: false }));
  complexionOptions: UserFilterOption[] = COMPLEXIONS.map((value) => ({ value, label: value, checked: false }));
  skillOptions: UserFilterOption[] = OTHER_SKILLS.map((value) => ({ value, label: value, checked: false }));
  ageRange = {
    lower: 18,
    upper: 100,
  };
  heightRange = {
    lower: 3,
    upper: 8,
  };
  weightRange = {
    lower: 50,
    upper: 500,
  };
  rating = 0;

  ngOnInit() {
    this.applyInitialFilters();
  }

  private applyInitialFilters() {
    if (!this.filters) return;

    this.setCheckedValues(this.genderOptions, this.filters.gender);
    this.setCheckedValues(this.ethnicityOptions, this.filters.ethnicity);
    this.setCheckedValues(this.complexionOptions, this.filters.complexion);
    const selectedSkills = new Set(profileSkillKeys(this.filters.skill));
    this.skillOptions.forEach((option) => (option.checked = selectedSkills.has(normalizeProfileSkill(option.value))));
    this.emailVerified = this.filters.emailVerified ?? this.emailVerified;
    this.subscriber = this.filters.subscriber ?? this.subscriber;
    this.verifiedAccount = this.filters.verifiedAccount ?? this.verifiedAccount;
    this.sortMode = this.filters.sortMode || this.filters.sortBy || this.sortMode;
    this.heightRange = {
      lower: this.filters.minHeight ? this.filters.minHeight / 12 : this.heightRange.lower,
      upper: this.filters.maxHeight ? this.filters.maxHeight / 12 : this.heightRange.upper,
    };
    this.weightRange = {
      lower: this.filters.minWeight ?? this.weightRange.lower,
      upper: this.filters.maxWeight ?? this.weightRange.upper,
    };
    this.ageRange = {
      lower: this.filters.minAge ?? this.ageRange.lower,
      upper: this.filters.maxAge ?? this.ageRange.upper,
    };
    this.rating = this.filters.rating ?? this.rating;
  }

  applyFilters() {
    this.modalCtrl.dismiss({
      filters: {
        gender: this.selectedValues(this.genderOptions),
        ethnicity: this.selectedValues(this.ethnicityOptions),
        complexion: this.selectedValues(this.complexionOptions),
        skill: this.selectedValues(this.skillOptions),
        emailVerified: this.emailVerified,
        subscriber: this.subscriber,
        verifiedAccount: this.verifiedAccount,
        minHeight: this.heightRange.lower * 12,
        maxHeight: this.heightRange.upper * 12,
        minWeight: this.weightRange.lower,
        maxWeight: this.weightRange.upper,
        minAge: this.ageRange.lower,
        maxAge: this.ageRange.upper,
        rating: this.rating,
        sortMode: this.sortMode,
      },
    });
  }

  updateOption(option: UserFilterOption, event: CustomEvent) {
    option.checked = Boolean(event.detail.checked);
  }

  private selectedValues(options: UserFilterOption[]): string[] {
    return options.filter((option) => option.checked).map((option) => option.value);
  }

  private setCheckedValues(options: UserFilterOption[], values: any) {
    const selected = new Set(this.toArrayFilter(values));
    options.forEach((option) => {
      option.checked = selected.has(option.value);
    });
  }

  private toArrayFilter(value: any): string[] {
    if (Array.isArray(value)) {
      return value.filter((item) => item !== undefined && item !== null && item !== '' && item !== 'All');
    }
    if (value === undefined || value === null || value === '' || value === 'All') return [];
    return [String(value)];
  }

  clearFilters() {
    this.modalCtrl.dismiss({ clear: true });
  }

  dismiss() {
    this.modalCtrl.dismiss();
  }
}
